import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import { useTranslation } from "react-i18next";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsBackButton from "../../athlete-portal/metrics/metrics-back-button";
import set from "@rill/set";
import { getChartLabels } from "../../../utils/chart-labels";
import NavigationComponent from "../../athlete-portal/calendar/components/NavigationComponent";
import SvgIcon from "../../../parts/SvgIcon";
import { ReportsBackHandler } from "../../athlete-portal/calendar/components/ReportsBackHandler";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { isPushOrPerformClient } from "../../../utils/user-helpers";

const {
  ZONES,
  PERIOD_FORMATS,
  DATE_FORMATS: { DATE_PICKER_FORMAT },
} = require("../../../constants");

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("dashboard");

  const {
    exercisesVolumeGroupZones,
    exercisesVolumeMean,
    query,
    search,
    feBranding = {},
  } = locals;

  const { period, units, stack, type, mobileWV } = query;

  const period_short = PERIOD_FORMATS[period];
  const selectedMetrics =
    query.type === "total"
      ? exercisesVolumeGroupZones
      : _.pick(exercisesVolumeGroupZones, [query.type]);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();

  const labels = getChartLabels(keys, query, period);

  const selectedMetricsGroups = _.values(selectedMetrics);
  const volumeSeries = _.map(ZONES, (zone) => {
    return {
      name: zone,
      className: stack ? zone : type,
      data: _.map(
        keys,
        (key) =>
          locals.fromMetric(
            _.sumBy(selectedMetricsGroups, `${key}.${zone}`),
            units
          ) || 0
      ),
    };
  });
  const totalVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(volumeSeries, `data.${i}`))
  );

  const baselineVolume = locals.fromMetric(
    _.get(exercisesVolumeMean, query.type),
    units
  );
  const baselineSeries = _.map(keys, () => baselineVolume);
  const maxValue = _.max(totalVolumes.concat(baselineVolume));

  return (
    <main
      role="reports-wrapper"
      className="Main Metrics"
      data-theme={mobileWV ? feBranding.appTheme : undefined}
      style={
        mobileWV
          ? {
              backgroundColor: "var(--background-color)",
              maxHeight: "unset",
              height: "100vh",
            }
          : null
      }
    >
      <NavigationComponent
        query={query}
        pathName={"/dashboard/metrics"}
        text={t("dashboard:metrics.calendarDayView")}
        classNames="mobile-hidden"
      />
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("dashboard:metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/dashboard/metrics/${search}${
            mobileWV ? "&&randomReload=true" : ""
          }`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/dashboard/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/baseline-vol-rel-intensity-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/baseline-vol-rel-intensity-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3>{t("dashboard:baselineVolume.title")}</h3>
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("dashboard:baselineVolume.kg"), value: "metric" },
                  { text: t("dashboard:baselineVolume.lbs"), value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                {t("dashboard:baselineVolume.description")}
              </p>
            </div>
            <p className="p4 only-show-mobile">
              {t("dashboard:baselineVolume.description")}
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder={t("dashboard:metrics.startDate")}
                value={query.start}
                max={query.end}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>{t("dashboard:metrics.to")}</span>
              <DateInput
                name="end"
                placeholder={t("dashboard:metrics.endDate")}
                value={query.end}
                min={query.start}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
            <div>
              <ToggleLinksInput
                name="period"
                options={[
                  { text: t("dashboard:metrics.month"), value: "month" },
                  { text: t("dashboard:metrics.week"), value: "week" },
                  { text: t("dashboard:metrics.day"), value: "day" },
                ]}
                activePrimary={true}
              />
            </div>
          </hgroup>
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: t("dashboard:baselineVolume.kg"), value: "metric" },
                { text: t("dashboard:baselineVolume.lbs"), value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />
            <ToggleLinksInput
              name="baselineTimeSpan"
              options={[
                { text: t("dashboard:baselineVolume.sixMonths"), value: "6-months" },
                { text: t("dashboard:baselineVolume.threeMonths"), value: "3-months" },
                { text: t("dashboard:baselineVolume.oneMonth"), value: "1-month" },
              ]}
              className={"mobile-hidden"}
            />
            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("dashboard:baselineVolume.total"), value: "total" },
                { text: t("dashboard:baselineVolume.squat"), value: "squat" },
                { text: t("dashboard:baselineVolume.bench"), value: "bench" },
                { text: t("dashboard:baselineVolume.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>
        <div className="Chart-Frame bar-chart">
          <div className="body">
            {!_.isEmpty(selectedMetrics) && (
              <div>
                <div className="legend alt">
                  <div className="border">
                    <a
                      className="checkbox-input"
                      href={`/actions/refresh?stack=${!stack}`}
                    >
                      <input type="checkbox" checked={stack} readOnly={true} />
                      <span className="checkbox" />
                    </a>

                    {_.map(ZONES, (zone) => {
                      return (
                        <div key={zone} className={`bar series ${zone}`}>
                          {t(`dashboard:baselineVolume.zone${zone.charAt(0).toUpperCase() + zone.slice(1)}`)}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="axis-labels">
                  <div className="left">{t("dashboard:baselineVolume.volume")}</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: volumeSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          onlyInteger: true,
                          scaleMinSpace: 30,
                          high: maxValue,
                          low: 0,
                        },
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: labels,
                        series: volumeSeries,
                      }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        stackBars: true,
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          low: 0,
                          high: maxValue,
                          showGrid: true,
                          showLabel: false,
                          scaleMinSpace: 30,
                        },
                      }}
                      tooltip={(item, i) => {
                        const key = keys[i];
                        const date = moment
                          .utc(query.start, DATE_PICKER_FORMAT)
                          .add(key, period);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date.clone().add(1, period).subtract(1, "day"),
                          ],
                          query
                        );
                        const totalVolume = totalVolumes[i];
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <div>
                                <b>{dayRange}</b>
                              </div>

                              <div>
                                {t("dashboard:baselineVolume.volume")}{" "}
                                <b>{locals.withWeight(totalVolume, units)}</b>
                              </div>
                              <div>
                                {t("dashboard:baselineVolume.baselineVolume")}{" "}
                                <b>
                                  {locals.withWeight(baselineVolume, units)}
                                </b>
                              </div>
                              {_.map(volumeSeries, (series) => {
                                return series.data[i] ? (
                                  <div>
                                    <b>{t(`dashboard:baselineVolume.zone${series.name.charAt(0).toUpperCase() + series.name.slice(1)}`)}</b>{" "}
                                    {_.round(
                                      (series.data[i] / totalVolume) * 100,
                                      2
                                    )}
                                    %
                                  </div>
                                ) : (
                                  false
                                );
                              })}
                            </div>
                          </div>
                        );
                      }}
                    />
                    {_.size(keys) >= 2 && (
                      <Chart
                        type="Line"
                        data={{ series: [baselineSeries] }}
                        options={{
                          width: `${_.size(keys) * 40}px`,
                          height: "380px",
                          showPoint: false,
                          lineSmooth:
                            process.browser &&
                            require("chartist").Interpolation.simple(),
                          chartPadding: { left: 35, right: 75 },
                          axisX: { showGrid: false, showLabel: false },
                          axisY: {
                            low: 0,
                            high: maxValue,
                            showGrid: false,
                            showLabel: false,
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </main>
  );
}

function handleDayChange(date, type) {
  // Creating the new query params (type=date) e.g.: start=June 21, 2024
  const searchString = `${encodeURIComponent(type)}=${encodeURIComponent(
    date
  )}`;

  // Creating a new <a> element to navigate to the new URL throught it
  // (tried with submit-form and didn't work)
  var linkElement = document.createElement("a");

  // Setting its href attribute to the new URL
  linkElement.href = `/actions/refresh?${searchString}`;

  // Appending it to the body and simulating the click
  document.body.appendChild(linkElement);
  linkElement.click();
  document.body.removeChild(linkElement);
}
const BaselineVolumeRelativeIntensity = (app) => {
  app.get(
    "/baseline-volume-relative-intensity",
    set("heading", "Baseline Volume & Relative Intensity"),
    async ({ req, res, load, locals, api, fail }) => {
      const { query } = req;

      _.defaults(req.query, {
        type: "total",
        stack: true,
        period: "day",
        units: locals.units,
        baselineTimeSpan: "6-months",
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await Promise.all([
        load("exercisesVolumeGroupZones", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
        }),
        load("exercisesVolumeMean", {
          period: query.period,
          timeSpan: query.baselineTimeSpan,
          day: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
        }),
      ]);

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  type: "baseline-volume-relative-intensity",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick([
                        "exercisesVolumeGroupZones",
                        "exercisesVolumeMean",
                      ])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }
      const { activeUser: user } = locals;
      if (isPushOrPerformClient(user) || user.coach) {
        await load("feBranding", {});
      }
      locals.modal = {
        share: <ShareModal />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default BaselineVolumeRelativeIntensity;
